import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  getTransactionLogDetails,
  reset,
  clearDetails
} from "redux/admin/transaction-logs/transactionLogsSlice";
import { toast } from "react-toastify";
import { FiArrowLeft, FiUser, FiDollarSign, FiClock, FiFileText } from "react-icons/fi";

const TransactionLogDetailContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logId } = useParams();
  
  const {
    transactionLogDetails,
    isDetailsLoading,
    isDetailsError,
    isDetailsSuccess,
    message
  } = useSelector((state) => state.adminTransactionLogs);

  useEffect(() => {
    if (logId) {
      dispatch(getTransactionLogDetails(logId));
    }
    
    return () => {
      dispatch(clearDetails());
    };
  }, [dispatch, logId]);

  useEffect(() => {
    if (isDetailsError) {
      toast.error(message || "İşlem logu detayları alınamadı!");
      dispatch(reset());
    }
  }, [dispatch, isDetailsError, message]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  const getTransactionTypeLabel = (txtype) => {
    const types = {
      claim_payment: "Bayi Tarafından Dekont Girildi",
      subreseller_claim_payment: "Alt Bayi Tarafından Dekont Girildi",
      reseller_transfer: "Bayi Elmas Satın Alma",
      retail_order: "Perakende Sipariş",
      subreseller_retail_order: "Alt Bayi Perakende Sipariş",
      payout: "Ödeme Talebi",
      revert: "İptal/İade",
      misc: "Diğer"
    };
    return types[txtype] || txtype;
  };

  if (isDetailsLoading) {
    return (
      <div className="bg-black min-h-screen p-4 flex items-center justify-center">
        <div className="text-white-100">İşlem logu detayları yükleniyor...</div>
      </div>
    );
  }

  if (!transactionLogDetails) {
    return (
      <div className="bg-black min-h-screen p-4">
        <div className="text-center py-8 text-gray-400">
          İşlem logu bulunamadı.
        </div>
      </div>
    );
  }

  const log = transactionLogDetails;

  return (
    <div className="bg-black min-h-screen p-4">
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/transaction-logs')}
          className="flex items-center gap-2 text-white-100 hover:text-secondary transition-all mb-4"
        >
          <FiArrowLeft size={20} />
          İşlem Loglarına Dön
        </button>
        
        <h2 className="text-2xl font-semibold text-white-100">
          İşlem Logu Detayları
        </h2>
        <p className="text-gray-400 text-sm mt-1">
          ID: {log.id}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Information */}
        <div className="bg-gray-800 p-6 rounded-md">
          <h3 className="text-lg font-semibold text-white-100 mb-4 flex items-center gap-2">
            <FiFileText size={20} />
            Temel Bilgiler
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="text-gray-400 text-sm">İşlem Tipi</label>
              <div className="text-white-100 font-medium">
                {getTransactionTypeLabel(log.txtype)}
              </div>
            </div>
            
            <div>
              <label className="text-gray-400 text-sm">Oluşturulma Tarihi</label>
              <div className="text-white-100">{formatDate(log.created_at)}</div>
            </div>
            
            <div>
              <label className="text-gray-400 text-sm">Güncellenme Tarihi</label>
              <div className="text-white-100">{formatDate(log.updated_at)}</div>
            </div>
            
            {log.refid && (
              <div>
                <label className="text-gray-400 text-sm">Referans ID</label>
                <div className="text-white-100 font-mono text-sm">{log.refid}</div>
              </div>
            )}
            
            {log.message && (
              <div>
                <label className="text-gray-400 text-sm">Mesaj</label>
                <div className="text-white-100 bg-gray-700 p-3 rounded text-sm">
                  {log.message}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* User Information */}
        <div className="bg-gray-800 p-6 rounded-md">
          <h3 className="text-lg font-semibold text-white-100 mb-4 flex items-center gap-2">
            <FiUser size={20} />
            Kullanıcı Bilgileri
          </h3>
          
          {log.user_data && (
            <div className="space-y-4">
              <div>
                <label className="text-gray-400 text-sm">İsim</label>
                <div className="text-white-100 font-medium">{log.user_data.name}</div>
              </div>
              
              <div>
                <label className="text-gray-400 text-sm">Tip</label>
                <div className="text-white-100 capitalize">{log.user_data.type}</div>
              </div>
              
              <div>
                <label className="text-gray-400 text-sm">Kullanıcı ID</label>
                <div className="text-white-100 font-mono text-sm">{log.user_data.id}</div>
              </div>
              
              {log.user_data.retail_commission_rate && (
                <div>
                  <label className="text-gray-400 text-sm">Komisyon Oranı</label>
                  <div className="text-white-100">{log.user_data.retail_commission_rate}%</div>
                </div>
              )}
              
              <div>
                <label className="text-gray-400 text-sm">Mevcut Bakiye</label>
                <div className="text-white-100 font-medium">₺{formatAmount(log.user_data.balance_try)}</div>
              </div>
            </div>
          )}
        </div>

        {/* Amount Information */}
        <div className="bg-gray-800 p-6 rounded-md">
          <h3 className="text-lg font-semibold text-white-100 mb-4 flex items-center gap-2">
            <FiDollarSign size={20} />
            Tutar Bilgileri
          </h3>
          
          <div className="space-y-4">
            {log.amount_diamonds && (
              <div>
                <label className="text-gray-400 text-sm">Elmas</label>
                <div className="text-white-100 font-medium text-lg">
                  {log.amount_diamonds} 💎
                </div>
              </div>
            )}
            
            {log.amount_try && (
              <div>
                <label className="text-gray-400 text-sm">Tutar (TL)</label>
                <div className="text-white-100 font-medium text-lg">
                  ₺{formatAmount(log.amount_try)}
                </div>
              </div>
            )}
            
            <div>
              <label className="text-gray-400 text-sm">Önceki Bakiye</label>
              <div className="text-white-100">₺{formatAmount(log.balance_before)}</div>
            </div>
            
            <div>
              <label className="text-gray-400 text-sm">Sonraki Bakiye</label>
              <div className="text-white-100">₺{formatAmount(log.balance_after)}</div>
            </div>
            
            <div>
              <label className="text-gray-400 text-sm">Bakiye Değişimi</label>
              <div className={`font-medium ${
                log.balance_after > log.balance_before ? 'text-green-400' :
                log.balance_after < log.balance_before ? 'text-red-400' : 'text-white-100'
              }`}>
                {log.balance_after > log.balance_before ? '+' : ''}
                ₺{formatAmount(log.balance_after - log.balance_before)}
              </div>
            </div>
          </div>
        </div>

        {/* Related Information */}
        <div className="bg-gray-800 p-6 rounded-md">
          <h3 className="text-lg font-semibold text-white-100 mb-4 flex items-center gap-2">
            <FiClock size={20} />
            İlgili Bilgiler
          </h3>
          
          <div className="space-y-4">
            {log.order_id && (
              <div>
                <label className="text-gray-400 text-sm">Sipariş ID</label>
                <div className="text-white-100 font-mono text-sm">{log.order_id}</div>
                {log.order && (
                  <div className="mt-2 text-sm text-gray-300">
                    <div>BIGO Kullanıcı Adı: {log.order.bigo_username}</div>
                    <div>Durum: {log.order.status}</div>
                    <div>Elmas: {log.order.amount_diamonds}</div>
                  </div>
                )}
              </div>
            )}
            
            {log.payment_id && (
              <div>
                <label className="text-gray-400 text-sm">Ödeme ID</label>
                <div className="text-white-100 font-mono text-sm">{log.payment_id}</div>
                {log.payment && (
                  <div className="mt-2 text-sm text-gray-300">
                    <div>Ad Soyad: {log.payment.full_name}</div>
                    <div>Tutar: ₺{formatAmount(log.payment.amount_try)}</div>
                    {log.payment.reference1 && <div>Ref1: {log.payment.reference1}</div>}
                    {log.payment.reference2 && <div>Ref2: {log.payment.reference2}</div>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionLogDetailContainer;